/**
 * @file Handles form validation and keeps floating labels floated with entered text
 * @author michael.schmid@notch-interactive.com
 *
 * @module modules/m21-form
 *
 * For Datepicker language options see: https://www.npmjs.com/package/vuejs-datepicker
 *
 * This is an example how to pass validation rules to the components:
 * validation = "'required|date|dateinrange:-10,10'"
 *
 * The following validation rules can be applied:
 *
 * required - checks if field is not empty
 * date - checks for valid date
 * dateinrange:(-10,10) - two number values are required - first one indicates lower bound in days from today, second one the upper bound. In this case the validation checks if the entered date lies within 10 days before today and 10 days after today.
 * email - checks for valid email address
 * max:5 - checks for maximal number of characters
 * min:5 - checks for minimal number of characters
 *
 * More rules can be found here, but are not tested yet:
 * http://vee-validate.logaretm.com/rules.html#available-validations
 *
 */


// Instantiate VeeValidate and update dictionary with messages and attributes
// const dictionary = {
//     en: {
//         messages: window.DS.translations.validationErrorMessages,
//         attributes: window.DS.translations.validationAttributes
//     }
// };

// Vue.use(VeeValidate);
// Validator.updateDictionary(dictionary);


import Vue from 'vue/dist/vue.min.js';
// import Datepicker from 'vuejs-datepicker';
import floatingLabel from '../components/floating-label';
import { splitDateParts, transformDate } from '../helpers/date-helpers';
import autosize from '../libs/autosize';
import VeeValidate, { Validator } from 'vee-validate';

import form from '../components/form';

const instance = {},
    defaults = {
        formContainer: '.m-form',
        boundAttr: 'data-form-bound'
    };

let forms = {},
    dictionary = {},
    vueInitialized = false;

instance.init = function (options) {
    let settings = Object.assign({}, defaults, options),
        formContainers = document.querySelectorAll(settings.formContainer + ':not([' + settings.boundAttr + '])');

    if (window.DS && window.DS.translations && window.DS.translations.validationErrorMessages && window.DS.translations.validationAttributes && vueInitialized === false) {

        vueInitialized = true;

        dictionary = {
            en: {
                messages: window.DS.translations.validationErrorMessages,
                attributes: window.DS.translations.validationAttributes
            }
        };

        // Init validation (vue stuff)
        Vue.use(VeeValidate);
        Validator.updateDictionary(dictionary);

        Validator.extend('date', {
            getMessage: window.DS.translations.validationErrorMessages.date,
            validate: value => {
                let dateParts = splitDateParts(value),
                    date = transformDate(dateParts);

                let constructedDate = new Date(date.year, date.month, date.day);
                return constructedDate && ((constructedDate.getMonth() + 1) === Number(dateParts[1]));
            }
        });

        Validator.extend('dateinrange', {
            getMessage: window.DS.translations.validationErrorMessages.dateinrange,
            validate: (value, args) => {
                let lowerBound = Number(args[0]),
                    upperBound = Number(args[1]),
                    dateParts = splitDateParts(value),
                    date = transformDate(dateParts);

                let constructedDate = new Date(date.year, date.month, date.day),
                    today = new Date();

                let lowerBoundDate = new Date(new Date(today).setDate(today.getDate() + lowerBound));
                let upperBoundDate = new Date(new Date(today).setDate(today.getDate() + upperBound));

                return (lowerBoundDate <= constructedDate) && (constructedDate <= upperBoundDate);
            }
        });
    }

    // Add forms to collection
    for (let i = formContainers.length; i--;) {

        // Set the bound Attribute, so that this module doesn't initiate the factory multiple times over the same elements
        formContainers[i].setAttribute(settings.boundAttr, '');

        let formEl = formContainers[i].querySelector('form');

        // Check if form shall be validated
        if (formEl.id && !formContainers[i].hasAttribute('data-disable-validation') && !forms.hasOwnProperty(formEl.id)) {
            forms[formEl.id] = form(formContainers[i]);
        }
    }

    floatingLabel.init();
    autosize(document.querySelectorAll('textarea'));
};

/**
 * Get form instance, so events can be attached to it for example.
 *
 * @param {string} id - Form ID
 * @return {Object} - If id of form is given this instance will be returned. If no Id is passed, the entire collection is returned
 */
instance.get = (id) => {
    return !id ? forms : forms[id];
};

export default instance;

// export forms object to window
window.DS = window.DS || {};
window.DS.forms = instance;
